<template>
  <page>
    <div class="pane-container" style="background: #fff; padding: 12px">
      <SearchRenderer :query="searchForm" :schema="searchSchema" @search="handleSearch" @reset="handleReset">
      </SearchRenderer>
    </div>

    <div class="pane-container" style="background: #fff; padding: 12px; margin-top: 12px">
      <div class="actions" style="margin-bottom: 12px">
        <el-button type="primary" size="mini" @click="handleAddCategory">新增分类</el-button>
        <el-button type="primary" size="mini" @click="handleAddQuestion">新增问题</el-button>
      </div>

      <el-row :gutter="16">
        <el-col :span="8">
          <el-table
            :data="categoryList"
            border
            stripe
            size="small"
            highlight-current-row
            style="width: 100%"
            @current-change="handleCategoryCurrentChange"
          >
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column prop="type_name" label="分类名称" min-width="120">
              <template #default="{ row }">
                <LanguageInputEcho :obj="row.type_name" />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right" align="center">
              <template #default="{ row }">
                <el-button type="text" size="small" @click="handleEditCategory(row)">编辑</el-button>
                <el-button type="text" size="small" class="danger-button" @click="handleDeleteCategory(row)"
                  >删除</el-button
                >
              </template>
            </el-table-column>
          </el-table>
        </el-col>
        <el-col :span="16">
          <el-table
            v-loading="questionLoading"
            :data="questionList"
            border
            stripe
            size="small"
            highlight-current-row
            style="width: 100%"
          >
            <el-table-column type="index" label="序号" width="60" align="center" />
            <el-table-column prop="title" label="问题标题" min-width="200" show-overflow-tooltip>
              <template #default="{ row }">
                <LanguageInputEcho :obj="row.title" />
              </template>
            </el-table-column>
            <el-table-column prop="is_hot" label="是否热门" width="90">
              <template #default="{ row }">
                <el-tag :type="row.is_hot === 1 ? 'success' : 'info'" size="small">
                  {{ row.is_hot === 1 ? '是' : '否' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="operator" label="编辑人" min-width="100" />
            <el-table-column prop="updated_at" label="编辑时间" width="140">
              <template #default="{ row }">
                {{ row.updated_at ? parseTime(row.updated_at) : '--' }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="80" align="center">
              <template #default="{ row }">
                <el-tag :type="row.status === 1 ? 'success' : 'info'" size="small">
                  {{ row.status === 1 ? '启用' : '停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right" align="center">
              <template #default="{ row }">
                <el-button type="text" size="small" @click="handleEditQuestion(row)">编辑</el-button>
                <el-button type="text" size="small" class="danger-button" @click="handleDeleteQuestion(row)"
                  >删除</el-button
                >
              </template>
            </el-table-column>
          </el-table>
          <div style="text-align: right; margin-top: 15px">
            <el-pagination
              :current-page="questionPage"
              :page-sizes="[10, 20, 30, 50]"
              :page-size="questionPageSize"
              layout="total, sizes, prev, pager, next, jumper"
              :total="questionTotal"
              @size-change="handleQuestionSizeChange"
              @current-change="handleQuestionPageChange"
            />
          </div>
        </el-col>
      </el-row>
    </div>

    <FaqCategoryDialog
      :visible.sync="categoryDialogVisible"
      :dialog-title="categoryDialogTitle"
      :form-data="currentCategoryForm"
      @success="handleCategoryDialogSuccess"
    />
    <FaqQuestionDialog
      :visible.sync="questionDialogVisible"
      :dialog-title="questionDialogTitle"
      :form-data="currentQuestionForm"
      :category-options="categoryOptions"
      @success="handleQuestionDialogSuccess"
    />
  </page>
</template>

<script setup>
import { deleteFaqCategory, deleteFaqQuestion, getFaqCategoryList, getFaqQuestionList } from '@/api/content/faq'
import { parseTime } from '@/utils'
import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue'
import FaqCategoryDialog from './components/FaqCategoryDialog.vue'
import FaqQuestionDialog from './components/FaqQuestionDialog.vue'
import LanguageInputEcho from '@/components/LanguageInputEcho'
import SearchRenderer from '@/components/SchemaTablePage/SearchRenderer.vue'

const { proxy } = getCurrentInstance()

const searchForm = reactive({
  title: '',
  is_hot: '',
  status: '',
})

const HOT_OPTIONS = [
  { key: 'all', label: '全部', value: '' },
  { key: 'yes', label: '是', value: 1 },
  { key: 'no', label: '否', value: 2 },
]

const STATUS_OPTIONS = [
  { key: 'all', label: '全部', value: '' },
  { key: 'on', label: '启用', value: 1 },
  { key: 'off', label: '停用', value: 2 },
]

const searchSchema = [
  {
    key: 'title',
    prop: 'title',
    label: '标题',
    component: 'input',
    componentProps: { placeholder: '请输入标题', clearable: true },
  },
  {
    key: 'is_hot',
    prop: 'is_hot',
    label: '是否热门',
    component: 'select',
    componentProps: { placeholder: '全部', clearable: true },
    options: HOT_OPTIONS,
  },
  {
    key: 'status',
    prop: 'status',
    label: '状态',
    component: 'select',
    componentProps: { placeholder: '全部', clearable: true },
    options: STATUS_OPTIONS,
  },
  { key: 'actions', component: 'actions' },
]

const categoryList = ref([])
const categoryLoading = ref(false)
const categoryDialogVisible = ref(false)
const categoryDialogTitle = ref('新增分类')
const currentCategoryForm = ref({})

const questionList = ref([])
const questionLoading = ref(false)
const questionPage = ref(1)
const questionPageSize = ref(10)
const questionTotal = ref(0)
const selectedCategoryId = ref(null)
const questionDialogVisible = ref(false)
const questionDialogTitle = ref('新增问题')
const currentQuestionForm = ref({})

const categoryOptions = computed(() =>
  categoryList.value.map(item => ({ label: item.type_name, type_name: item.type_name, value: item.id }))
)

function fetchCategoryList() {
  categoryLoading.value = true
  getFaqCategoryList()
    .then(res => {
      const data = res.data
      if (Array.isArray(data)) {
        categoryList.value = data
      } else if (data && Array.isArray(data.list)) {
        categoryList.value = data.list
      } else {
        categoryList.value = []
      }
    })
    .catch(error => {
      console.error('获取常见问题分类失败', error)
      proxy.$message.error(error?.response?.data?.message || '获取分类失败')
    })
    .finally(() => {
      categoryLoading.value = false
    })
}

function fetchQuestionList() {
  questionLoading.value = true
  const params = {
    page: questionPage.value,
    page_size: questionPageSize.value,
    title: searchForm.title || undefined,
    is_hot: searchForm.is_hot !== '' ? searchForm.is_hot : undefined,
    status: searchForm.status !== '' ? searchForm.status : undefined,
    type_id: selectedCategoryId.value || undefined,
  }
  getFaqQuestionList(params)
    .then(res => {
      const data = res.data || {}
      questionList.value = Array.isArray(data.list) ? data.list : []
      questionTotal.value = typeof data.total === 'number' ? data.total : 0
    })
    .catch(error => {
      console.error('获取常见问题列表失败', error)
      proxy.$message.error(error?.response?.data?.message || '获取问题列表失败')
    })
    .finally(() => {
      questionLoading.value = false
    })
}

function handleSearch() {
  questionPage.value = 1
  fetchQuestionList()
}

function handleReset() {
  searchForm.title = ''
  searchForm.is_hot = ''
  searchForm.status = ''
  questionPage.value = 1
  fetchQuestionList()
}

function handleCategoryCurrentChange(row) {
  selectedCategoryId.value = row ? row.id : null
  questionPage.value = 1
  fetchQuestionList()
}

function handleAddCategory() {
  categoryDialogTitle.value = '新增分类'
  currentCategoryForm.value = {}
  categoryDialogVisible.value = true
}

function handleEditCategory(row) {
  categoryDialogTitle.value = '编辑分类'
  currentCategoryForm.value = { ...row }
  categoryDialogVisible.value = true
}

function handleDeleteCategory(row) {
  proxy
    .$confirm('确认删除该分类吗？', '提示', {
      type: 'warning',
    })
    .then(() => {
      return deleteFaqCategory(row.id)
    })
    .then(() => {
      proxy.$message.success('删除成功')
      fetchCategoryList()
      if (selectedCategoryId.value === row.id) {
        selectedCategoryId.value = null
        fetchQuestionList()
      }
    })
    .catch(() => {})
}

function handleCategoryDialogSuccess() {
  fetchCategoryList()
}

function handleAddQuestion() {
  questionDialogTitle.value = '新增问题'
  currentQuestionForm.value = {}
  questionDialogVisible.value = true
}

function handleEditQuestion(row) {
  questionDialogTitle.value = '编辑问题'
  currentQuestionForm.value = { ...row }
  questionDialogVisible.value = true
}

function handleDeleteQuestion(row) {
  proxy
    .$confirm('确认删除该问题吗？', '提示', {
      type: 'warning',
    })
    .then(() => {
      return deleteFaqQuestion(row.id)
    })
    .then(() => {
      proxy.$message.success('删除成功')
      fetchQuestionList()
    })
    .catch(() => {})
}

function handleQuestionDialogSuccess() {
  fetchQuestionList()
}

function handleQuestionSizeChange(val) {
  questionPageSize.value = val
  questionPage.value = 1
  fetchQuestionList()
}

function handleQuestionPageChange(val) {
  questionPage.value = val
  fetchQuestionList()
}

onMounted(() => {
  fetchCategoryList()
  fetchQuestionList()
})
</script>

<style lang="scss" scoped>
.pane-container {
  background: #fff;
  padding: 12px;
}
.actions {
  margin-bottom: 12px;
}
</style>
